import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  CircularProgress,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Paper,
} from '@mui/material';
import {
  PlayArrow as ActiveIcon,
  Stop as StoppedIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Code as CodeIcon,
  Computer as ComputerIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { statsApi, runsApi } from '../services/api';
import { DashboardStats, Run, ActivityEvent, PaginatedResponse } from '../services/types';
import { formatDateSafe } from '../utils/dateFormat';
import {
  RunsOverTimeChart,
  WorkloadPieChart,
  SuccessRateTrendChart,
  VMUtilizationBarChart,
} from '../components/Charts';
import { DashboardSkeleton } from '../components/SkeletonLoaders';
import ErrorMessage from '../components/ErrorMessage';
import { RunsCostSummaryCard } from '../components/RunsCostSummaryCard';
import { useApiQuery } from '../hooks/useApiQuery';

export default function DashboardPage() {
  const [refreshing, setRefreshing] = useState(false);

  const { data: stats, loading: statsLoading, error: statsError, refetch: refetchStats } = useApiQuery<DashboardStats>(
    () => statsApi.getStats()
  );

  const { data: runsData, loading: runsLoading, refetch: refetchRuns } = useApiQuery<PaginatedResponse<Run>>(
    () => runsApi.list({ pageSize: 5 })
  );

  const loading = statsLoading || runsLoading;
  const error = statsError;
  const recentRuns = runsData?.items || [];

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(async () => {
      setRefreshing(true);
      await Promise.all([refetchStats(), refetchRuns()]);
      setRefreshing(false);
    }, 30000);
    return () => clearInterval(interval);
  }, [refetchStats, refetchRuns]);

  const handleRetry = async () => {
    await Promise.all([refetchStats(), refetchRuns()]);
  };

  const getActivityIcon = (event: ActivityEvent) => {
    switch (event.type) {
      case 'run_created':
        return <SuccessIcon color="success" fontSize="small" />;
      case 'run_stopped':
        return <StoppedIcon color="warning" fontSize="small" />;
      case 'run_expired':
        return <ErrorIcon color="error" fontSize="small" />;
      case 'script_executed':
        return <CodeIcon color="primary" fontSize="small" />;
      case 'script_failed':
        return <ErrorIcon color="error" fontSize="small" />;
      default:
        return <InfoIcon color="info" fontSize="small" />;
    }
  };

  if (loading && !stats) {
    return <DashboardSkeleton />;
  }

  if (error) {
    return (
      <ErrorMessage
        message={error}
        title="Fehler beim Laden des Dashboards"
        onRetry={handleRetry}
      />
    );
  }

  if (!stats) {
    return (
      <ErrorMessage
        message="Keine Dashboard-Daten verfügbar. Bitte versuchen Sie es später erneut."
        title="Keine Daten"
        severity="warning"
        onRetry={handleRetry}
      />
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Dashboard</Typography>
        <Box display="flex" alignItems="center" gap={1}>
          {refreshing && (
            <Box display="flex" alignItems="center" gap={0.5}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">
                Aktualisiere...
              </Typography>
            </Box>
          )}
          {!refreshing && (
            <Box display="flex" alignItems="center" gap={0.5}>
              <RefreshIcon fontSize="small" color="action" />
              <Typography variant="caption" color="text.secondary">
                Aktualisiert: {formatDateSafe(stats.timestamp, 'HH:mm:ss')}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/*
        NC-1288 T31: every figure in this section counts produktiv runs only — the
        API drops test/E2E runs in statsService. Stated once here instead of on each
        number: repeating "(ohne Testläufe)" six times reads as noise and people stop
        seeing it. Which individual run is classified how is visible on the run
        detail page, where it can also be corrected (#1319).
      */}
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        Alle Kennzahlen ohne Testläufe
      </Typography>

      {/* Statistics Cards Row 1 */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <ActiveIcon color="success" fontSize="large" />
                <Box flexGrow={1}>
                  <Typography color="text.secondary" variant="body2">
                    Aktive Läufe
                  </Typography>
                  <Typography variant="h4">{stats.runs.active}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    von {stats.runs.total} gesamt (ohne Testläufe)
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <StoppedIcon color="warning" fontSize="large" />
                <Box flexGrow={1}>
                  <Typography color="text.secondary" variant="body2">
                    Gestoppte Läufe
                  </Typography>
                  <Typography variant="h4">{stats.runs.stopped}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {stats.runs.expired} abgelaufen
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <PeopleIcon color="primary" fontSize="large" />
                <Box flexGrow={1}>
                  <Typography color="text.secondary" variant="body2">
                    Teilnehmer
                  </Typography>
                  <Typography variant="h4">{stats.participants.total}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {stats.participants.last7Days} letzte 7 Tage
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <TrendingUpIcon color="success" fontSize="large" />
                <Box flexGrow={1}>
                  <Typography color="text.secondary" variant="body2">
                    Success Rate
                  </Typography>
                  <Typography variant="h4">{stats.operations.successRate}%</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {stats.operations.failureCount} Fehler
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* VM Statistics Row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: 'info.50', borderLeft: '4px solid', borderColor: 'info.main' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <ComputerIcon color="info" fontSize="large" />
                <Box flexGrow={1}>
                  <Typography color="text.secondary" variant="body2">
                    Virtuelle Maschinen
                  </Typography>
                  <Typography variant="h4">{stats.vms.total}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {stats.vms.activeRuns} in aktiven Runs
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: 'success.50', borderLeft: '4px solid', borderColor: 'success.main' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <SuccessIcon color="success" fontSize="large" />
                <Box flexGrow={1}>
                  <Typography color="text.secondary" variant="body2">
                    Provisionierte VMs
                  </Typography>
                  <Typography variant="h4">{stats.vms.provisioned}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Bereit zur Nutzung
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: 'warning.50', borderLeft: '4px solid', borderColor: 'warning.main' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <ActiveIcon color="warning" fontSize="large" />
                <Box flexGrow={1}>
                  <Typography color="text.secondary" variant="body2">
                    VMs in Betrieb
                  </Typography>
                  <Typography variant="h4">{stats.vms.activeRuns}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Aktuell genutzt
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Statistics Cards Row 2 */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Workload-Verteilung
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">CAM (HyperMill)</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {stats.runs.byWorkload.cam} Runs
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={
                    stats.runs.total > 0
                      ? (stats.runs.byWorkload.cam / stats.runs.total) * 100
                      : 0
                  }
                  sx={{ height: 8, borderRadius: 1, mb: 2 }}
                />

                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">BIM (Inventor)</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {stats.runs.byWorkload.bim} Runs
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={
                    stats.runs.total > 0
                      ? (stats.runs.byWorkload.bim / stats.runs.total) * 100
                      : 0
                  }
                  color="secondary"
                  sx={{ height: 8, borderRadius: 1, mb: 2 }}
                />

                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">IT (IT-Dev)</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {stats.runs.byWorkload.it} Runs
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={
                    stats.runs.total > 0
                      ? (stats.runs.byWorkload.it / stats.runs.total) * 100
                      : 0
                  }
                  color="info"
                  sx={{ height: 8, borderRadius: 1 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Aktivität (letzte Zeit)
              </Typography>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.50' }}>
                    <Typography variant="h5" color="primary">
                      {stats.runs.last7Days}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Runs (7 Tage)
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'secondary.50' }}>
                    <Typography variant="h5" color="secondary">
                      {stats.runs.last30Days}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Runs (30 Tage)
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'info.50' }}>
                    <Typography variant="h5" color="info.main">
                      {stats.operations.last24Hours.total}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Operationen (24h)
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.50' }}>
                    <Typography variant="h5" color="success.main">
                      {stats.participants.last30Days}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Teilnehmer (30d)
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Cost Overview */}
      <Box mt={4} mb={3}>
        <Typography variant="h5" gutterBottom>
          Kosten-Übersicht
        </Typography>
        <RunsCostSummaryCard runs={recentRuns} />
      </Box>

      {/* Charts Section */}
      <Box mt={4} mb={2}>
        <Typography variant="h5" gutterBottom>
          Trends & Auslastung
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Runs Over Time Chart */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Runs über Zeit (letzte 30 Tage)
              </Typography>
              {stats.timeSeries?.runsPerDay && stats.timeSeries.runsPerDay.length > 0 ? (
                <RunsOverTimeChart data={stats.timeSeries.runsPerDay} />
              ) : (
                <Box display="flex" justifyContent="center" p={3}>
                  <Typography color="text.secondary">Keine Daten verfügbar</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Success Rate Trend Chart */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Erfolgsquote (letzte 8 Wochen)
              </Typography>
              {stats.timeSeries?.successRatePerWeek && stats.timeSeries.successRatePerWeek.length > 0 ? (
                <SuccessRateTrendChart data={stats.timeSeries.successRatePerWeek} />
              ) : (
                <Box display="flex" justifyContent="center" p={3}>
                  <Typography color="text.secondary">Keine Daten verfügbar</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Workload Distribution Pie Chart */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Workload-Verteilung
              </Typography>
              {stats.runs.byWorkload.cam > 0 ||
              stats.runs.byWorkload.bim > 0 ||
              stats.runs.byWorkload.it > 0 ? (
                <WorkloadPieChart
                  cam={stats.runs.byWorkload.cam}
                  bim={stats.runs.byWorkload.bim}
                  it={stats.runs.byWorkload.it}
                />
              ) : (
                <Box display="flex" justifyContent="center" p={3}>
                  <Typography color="text.secondary">Keine Runs vorhanden</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* VM Utilization Bar Chart */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                VM Auslastung pro Run (letzte 10 Runs)
              </Typography>
              {stats.timeSeries?.vmUtilizationPerRun && stats.timeSeries.vmUtilizationPerRun.length > 0 ? (
                <VMUtilizationBarChart data={stats.timeSeries.vmUtilizationPerRun} />
              ) : (
                <Box display="flex" justifyContent="center" p={3}>
                  <Typography color="text.secondary">Keine Daten verfügbar</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        {/* Recent Activity */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Letzte Aktivitäten
              </Typography>
              {stats.recentActivity.length === 0 ? (
                <Typography color="text.secondary">Keine Aktivitäten</Typography>
              ) : (
                <List dense>
                  {stats.recentActivity.map((activity, index) => (
                    <React.Fragment key={index}>
                      <ListItem>
                        <ListItemIcon>{getActivityIcon(activity)}</ListItemIcon>
                        <ListItemText
                          primary={activity.message}
                          secondary={formatDateSafe(activity.timestamp, 'dd.MM.yyyy HH:mm')}
                        />
                        {activity.runId && (
                          <Chip label={activity.runId} size="small" variant="outlined" />
                        )}
                      </ListItem>
                      {index < stats.recentActivity.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Runs */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Neueste Schulungsläufe
              </Typography>
              {recentRuns.length === 0 ? (
                <Typography color="text.secondary">Keine Läufe vorhanden</Typography>
              ) : (
                <Box sx={{ mt: 2 }}>
                  {recentRuns.map((run) => (
                    <Box
                      key={run.id}
                      sx={{
                        p: 2,
                        mb: 1,
                        borderRadius: 1,
                        bgcolor: 'background.default',
                        '&:hover': { bgcolor: 'action.hover', cursor: 'pointer' },
                      }}
                      onClick={() => (window.location.href = `/runs/${run.runId}`)}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="body1" fontWeight="bold">
                            {run.runId} - {run.course}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {run.accounts?.length || 0} Teilnehmer · Erstellt:{' '}
                            {formatDateSafe(run.createdAtUtc, 'dd.MM.yyyy')}
                          </Typography>
                        </Box>
                        <Box display="flex" gap={1}>
                          {/* NC-299 / NC-896 class: Phase-3 runs carry `workloadId`; the
                              legacy `workload` enum may be absent (and sparse/test docs
                              may have neither). Deriving the chip value defensively
                              (mirrors RunsCostSummaryCard) prevents `undefined.toUpperCase()`
                              from throwing and taking the whole dashboard down via the
                              ErrorBoundary. */}
                          <Chip
                            label={(run.workloadId ?? run.workload ?? '').toUpperCase()}
                            color={(run.workloadId ?? run.workload) === 'cam' ? 'primary' : 'secondary'}
                            size="small"
                          />
                          <Chip
                            label={run.status || 'active'}
                            color={
                              run.status === 'active'
                                ? 'success'
                                : run.status === 'stopped'
                                ? 'warning'
                                : 'default'
                            }
                            size="small"
                          />
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
